/* eslint-disable react-hooks/exhaustive-deps */
import React, {useContext, useEffect, useState} from "react"
import {Spin} from "antd"
import {useTranslation} from "react-i18next"

import {Label} from "components/inputs/Label"
import {BaseButton, BaseInput} from "components"
import {handleError, handleErrorMessage, toastError} from "helpers"
import {addressService, studentPaymentService} from "services"
import styles from "./DirectEmbedPayment.module.css"
import {GeneralPayment} from "types/student-account/ledger-accounts"
import {StudentPaymentContext} from "context/StudentPaymentContext"
import {StudentCharge} from "types/student-account/student-ledger"
import {PaymentPlanItem} from "types/student-account/payment-plan"
import {PaymentCreatedFromPlatform, StudentPaymentSupportedCurrency} from "types/student-account/student-payment"
import {KlassDropdown} from "components/Select"
import {chain, sortBy} from "lodash"
import {useModel} from "hooks"

type Props = {
    providerConfig: any
    studentId: number
    paymentAmount: number
    currency: StudentPaymentSupportedCurrency
    isCheckingOut: boolean
    selectedPayment?: GeneralPayment
    customNote: string
    setCurrencyMinAmount: Function
    payerFullName?: string
    selectedCharges?: StudentCharge[]
    selectedPaymentItems?: PaymentPlanItem[]
    academicYear?: number
}

interface FormCardInfo {
    firstName: string
    lastName: string
    zip: string
    cardNumber: string
    expiry: string
    cardCode: string
    country: string | any
    state: string | any
    city: string
    street: string
}

export const DirectEmbedPayment = (props: Props) => {
    const {
        providerConfig,
        studentId,
        paymentAmount,
        currency,
        isCheckingOut,
        selectedPayment,
        customNote,
        setCurrencyMinAmount,
        payerFullName,
        selectedCharges,
        selectedPaymentItems,
        academicYear
    } = props

    const {t} = useTranslation(["user", "common"])
    const model = useModel()
    const {setPaymentStatus, setPaymentError} = useContext(StudentPaymentContext)

    const [cardData, setCardData] = useState<FormCardInfo>({
        firstName: model.user?.firstName || "",
        lastName: model.user?.lastName || "",
        zip: model.user?.postalCode || "",
        cardNumber: "",
        expiry: "",
        cardCode: "",
        country: "",
        state: "",
        city: model.user?.city || "",
        street: model.user?.address || ""
    })
    const [studentPayment, setStudentPayment] = useState<any | undefined>()
    const [countries, setCountries] = useState([])
    const [states, setStates] = useState([])
    const [loading, setLoading] = useState(false)
    const [isDropDown, setIsDropDown] = useState(false)

    const searchCountries = async (search = "") => {
        try {
            const params = !search ? {} : {filter: {search}}
            const response = await addressService.getCountries(params)
            const countries = response.data
                .filter((country) => !!country.name)
                .map((country) => ({
                    value: country.code,
                    label: country.name,
                    id: country.countryId
                }))
            setCountries(countries)
            return countries
        } catch (e) {
            handleError(e)
        }
    }

    const searchStates = async (search = "", countryId = 0) => {
        try {
            const params = {
                filter: {
                    search,
                    countryId
                }
            }
            if (!search) {
                delete params.filter.search
            }
            const response = await addressService.getStates(params)
            const states = chain(response.data)
                .groupBy("type")
                .map((value, key) => ({
                    label: key,
                    options: sortBy(value, ["name"]).map((state) => ({value: state.stateCode, label: state.name}))
                }))
                .value()
            setStates(states)
            return states
        } catch (e) {
            handleError(e)
        }
    }

    useEffect(() => {
        searchCountries()
    }, [])

    useEffect(() => {
        if (providerConfig) {
            setCurrencyMinAmount(providerConfig.minimumChargeAmountInCentsForCurrency)
        }
    }, [providerConfig])

    useEffect(() => {
        if (model?.user?.country) {
            const foundCountry = countries.find((country: any) => country.label === model.user.country)
            if (foundCountry) {
                setCardData((prev) => ({
                    ...prev,
                    country: foundCountry
                }))
            }
        }
    }, [model?.user?.country, countries])

    useEffect(() => {
        if (model?.user?.state) {
            const foundState = states
                .flatMap((group: any) => group.options)
                .find((state: any) => state.label === model.user.state || state.value === model.user.state)
            if (foundState) {
                setCardData((prev) => ({
                    ...prev,
                    state: foundState
                }))
            }
        }
    }, [model?.user?.state, states])

    useEffect(() => {
        if (cardData.country?.label === "United States" || cardData.country === "United States") {
            setIsDropDown(true)
            searchStates("", cardData.country?.id || 0)
            if (typeof cardData.state === "string") {
                setCardData((prev) => ({
                    ...prev,
                    state: ""
                }))
            }
        } else {
            if (typeof cardData.state === "object") {
                setCardData((prev) => ({
                    ...prev,
                    state: ""
                }))
            }
            setIsDropDown(false)
        }
    }, [cardData.country])

    useEffect(() => {
        if (!studentId || !paymentAmount || !isCheckingOut) {
            setStudentPayment(undefined)
            return
        }
        ;(async () => {
            try {
                const {studentPayment} = await studentPaymentService.createDirectEmbedStudentPayment({
                    amountInCents: Math.round(paymentAmount * 100),
                    currency,
                    studentProfileId: studentId,
                    generalPaymentId: selectedPayment?.generalPaymentId,
                    payerFullName,
                    academicYear,
                    paymentPlanItems: selectedPaymentItems?.length
                        ? selectedPaymentItems
                              .filter((item) => item.paymentAmount)
                              .map((item) => ({
                                  paymentPlanId: item.paymentPlanId,
                                  paymentPlanItemId: item.paymentPlanItemId,
                                  amount: paymentAmount
                              }))
                        : undefined,
                    notes: `${selectedPayment?.name || ""}${customNote ? `: ${customNote}` : ""}`,
                    createdFromPlatform: PaymentCreatedFromPlatform.EdularWeb
                })
                if (selectedCharges?.length) {
                    await studentPaymentService.assignPaymentCharges(
                        studentPayment.paymentId,
                        selectedCharges.map((charge) => ({chargeId: charge.chargeId, amount: charge.paymentAmount}))
                    )
                }
                setStudentPayment(studentPayment)
            } catch (error) {
                handleError(error)
            }
        })()
    }, [
        studentId,
        isCheckingOut,
        paymentAmount,
        currency,
        selectedPayment,
        customNote,
        payerFullName,
        selectedCharges,
        selectedPaymentItems,
        academicYear
    ])

    const validateBeforeSubmit = () => {
        const attrs = {
            firstName: {type: "string", errorMessage: t("common:validation.cantEmpty", {field: "First Name"})},
            lastName: {type: "string", errorMessage: t("common:validation.cantEmpty", {field: "Last Name"})},
            zip: {type: "string", errorMessage: t("common:validation.cantEmpty", {field: "Zip"})},
            cardNumber: {type: "string", errorMessage: t("common:validation.cantEmpty", {field: "Card Number"})},

            expiry: {
                type: "dateMMYYYY",
                errorMessage: t("common:validation.invalidFormat", {field: "Expiry (MM/YYYY)"})
            },

            cardCode: {type: "string", errorMessage: t("common:validation.cantEmpty", {field: "Card Code"})},
            country: {type: "stringOrObject", errorMessage: t("common:validation.cantEmpty", {field: "Country"})},
            state: {type: "stringOrObject", errorMessage: t("common:validation.cantEmpty", {field: "State"})},
            city: {type: "string", errorMessage: t("common:validation.cantEmpty", {field: "City"})},
            street: {type: "string", errorMessage: t("common:validation.cantEmpty", {field: "Street"})}
        }

        for (let [key, rule] of Object.entries(attrs)) {
            const value = cardData[key]

            if (!value) {
                handleErrorMessage(rule.errorMessage)
                return true
            }

            if (rule.type === "string") {
                if (typeof value !== "string" || value.trim() === "") {
                    handleErrorMessage(rule.errorMessage)
                    return true
                }
            }

            if (rule.type === "stringOrObject") {
                if (typeof value === "string" && value.trim() === "") {
                    handleErrorMessage(rule.errorMessage)
                    return true
                }
                if (typeof value === "object" && Object.keys(value).length === 0) {
                    handleErrorMessage(rule.errorMessage)
                    return true
                }
            }

            if (rule.type === "dateMMYYYY") {
                const regex = /^(0[1-9]|1[0-2])\/\d{4}$/
                if (typeof value !== "string" || !regex.test(value.trim())) {
                    handleErrorMessage(rule.errorMessage)
                    return true
                }
            }

            if (rule.type === "array") {
                if (!Array.isArray(value) || value.length < 1) {
                    handleErrorMessage(rule.errorMessage)
                    return true
                }
            }

            if (rule.type === "object") {
                if (typeof value !== "object" || Object.keys(value).length < 1) {
                    handleErrorMessage(rule.errorMessage)
                    return true
                }
            }
        }

        return false
    }

    const handleSubmit = async (event) => {
        try {
            event.preventDefault()

            const {firstName, lastName, zip, cardNumber, cardCode, expiry} = cardData
            if (validateBeforeSubmit()) {
                return
            }

            setPaymentStatus("processing")
            setPaymentError(undefined)
            setLoading(true)

            const fullName = `${firstName} ${lastName}`
            const expiryData = (expiry ?? "").split("/")
            const cardExpMonth = expiryData[0]?.trim()
            const cardExpYear = expiryData[1]?.trim()

            const tokenizationResponse = await studentPaymentService.tokenizeCardDirectEmbedStudentPayment({
                paymentId: studentPayment.paymentId,
                cardNumber: (cardNumber ?? "").replace(/\s/g, ""),
                cardExpMonth: cardExpMonth.padStart(2, "0"),
                cardExpYear: cardExpYear,
                cardCvv: cardCode,
                billingDetails: {
                    payerFullName: fullName,
                    address: {
                        zipcode: zip,
                        country: typeof cardData.country === "object" ? cardData.country.value : cardData.country,
                        state: typeof cardData.state === "object" ? cardData.state.value : cardData.state,
                        city: cardData.city,
                        street: cardData.street
                    }
                }
            })

            // Access token located inside paymentResponse -> tv3.baseTokenResponse -> token
            const token = tokenizationResponse.paymentResponse?.["tv3.baseTokenResponse"]?.token

            if (!token?.number) {
                throw new Error(
                    tokenizationResponse.paymentResponse?.["tv3.baseTokenResponse"]?.detailReason ||
                        "Failed to tokenize card"
                )
            }

            const paymentResult = await studentPaymentService.makeDirectEmbedStudentPayment({
                studentProfileId: studentPayment.studentProfileId,
                paymentId: studentPayment.paymentId,
                tokenizedCard: token.number,
                payerFullName: fullName,
                cvv: cardCode
            })

            setPaymentStatus("confirmed")

            if (paymentResult) {
                window.location.href = `${window.location.origin}?paymentId=${studentPayment.paymentId}`
            }
        } catch (error: any) {
            toastError(error?.response?.data?.message ?? error.message ?? "Internal Server Error")
            setPaymentStatus("failed")
            setPaymentError(error.message ?? "Payment failed")
        } finally {
            setLoading(false)
        }
    }

    return (
        <>
            {!providerConfig || !studentPayment ? (
                <div>
                    <Spin />
                </div>
            ) : (
                <>
                    <div className={styles.payFields}>
                        <Label text={t("common:studentInfo.firstName")} required>
                            <BaseInput
                                onChange={(value) => setCardData({...cardData, firstName: value})}
                                value={cardData.firstName}
                            />
                        </Label>
                    </div>
                    <div className={styles.payFields}>
                        <Label text={t("common:studentInfo.lastName")} required>
                            <BaseInput
                                onChange={(value) => setCardData({...cardData, lastName: value})}
                                value={cardData.lastName}
                            />
                        </Label>
                    </div>
                    <div className={styles.payFields}>
                        <Label text={t("common:studentInfo.postalCode")} required>
                            <BaseInput
                                onChange={(value) => setCardData({...cardData, zip: value})}
                                value={cardData.zip}
                            />
                        </Label>
                    </div>
                    <div className={styles.payFields}>
                        <Label text={"Card Number"} required>
                            <BaseInput
                                onChange={(value) => {
                                    setCardData({
                                        ...cardData,
                                        cardNumber:
                                            (value ?? "").trim() !== ""
                                                ? value
                                                      .replace(/\s/g, "")
                                                      .match(/.{1,4}/g)
                                                      ?.join(" ") ?? value
                                                : value
                                    })
                                }}
                                value={cardData.cardNumber}
                                placeholder="xxxx xxxx xxxx xxxx"
                                maxLength={19}
                            />
                        </Label>
                    </div>
                    <div className={styles.payFields}>
                        <Label text={"Expiry"} required>
                            <BaseInput
                                onChange={(value) => {
                                    setCardData({
                                        ...cardData,
                                        expiry:
                                            (value ?? "").trim().length > 2
                                                ? value.replace(/\//g, "").replace(/^(.{2})/, "$1/")
                                                : value
                                    })
                                }}
                                value={cardData.expiry}
                                placeholder="mm/yyyy"
                                maxLength={7}
                            />
                        </Label>
                    </div>
                    <div className={styles.payFields}>
                        <Label text={"CVV Code"} required>
                            <BaseInput
                                onChange={(value) => setCardData({...cardData, cardCode: value})}
                                value={cardData.cardCode}
                                placeholder="xxx"
                                maxLength={3}
                            />
                        </Label>
                    </div>
                    <div className={styles.payFields}>
                        <Label text={t("user.country")} required>
                            <KlassDropdown
                                value={cardData.country}
                                placeholder={t("common:selectField.placeholder")}
                                onChange={(option: any) => setCardData({...cardData, country: option})}
                                valueKey="value"
                                labelKey="label"
                                options={countries}
                            />
                        </Label>
                    </div>
                    <div className={styles.payFields}>
                        <Label text={t("user.state")} required>
                            {isDropDown ? (
                                <KlassDropdown
                                    value={cardData.state}
                                    placeholder={t("common:selectField.placeholder")}
                                    onChange={(option: any) => setCardData({...cardData, state: option})}
                                    options={states}
                                    valueKey="value"
                                    labelKey="label"
                                    isGrouped
                                />
                            ) : (
                                <BaseInput
                                    onChange={(value) => setCardData({...cardData, state: value})}
                                    placeholder={t("user.state")}
                                    value={cardData.state}
                                />
                            )}
                        </Label>
                    </div>

                    <div className={styles.payFields}>
                        <Label text={t("user.city")} required>
                            <BaseInput
                                onChange={(value) => setCardData({...cardData, city: value})}
                                value={cardData.city}
                            />
                        </Label>
                    </div>
                    <div className={styles.payFields}>
                        <Label text={t("user.streetAddress")} required>
                            <BaseInput
                                onChange={(value) => setCardData({...cardData, street: value})}
                                value={cardData.street}
                            />
                        </Label>
                    </div>
                    <div className={styles.doneBtn}>
                        <BaseButton title={t("common:pay")} isActive loading={loading} onClick={handleSubmit} />
                    </div>
                </>
            )}
        </>
    )
}
